/**
 * ET delivery quote integrity helpers. A quote describes a separate, hypothetical
 * basket; these pure functions never read or change the shopper's real cart.
 * Reject incomplete carrier responses and adjusted baskets rather than display a
 * plausible price for different products, options, properties or quantities.
 */
(() => {
  'use strict';
  const MAX_LINES = 250;
  const MAX_QUANTITY = 2147483647;
  const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
  const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
  const fail = message => { throw new Error(message); };

  function noErrors(payload) {
    if (!object(payload)) fail('Invalid delivery response');
    if (own(payload, 'errors') && (!Array.isArray(payload.errors) || payload.errors.length)) {
      fail('Delivery service returned errors');
    }
  }

  /** Reject unsafe object keys before combining GraphQL incremental fragments. */
  function safeTree(value) {
    if (Array.isArray(value)) value.forEach(safeTree);
    else if (object(value)) {
      for (const key of Object.keys(value)) {
        if (['__proto__', 'prototype', 'constructor'].includes(key)) fail('Unsafe delivery response');
        safeTree(value[key]);
      }
    }
    return value;
  }

  function merge(target, incoming) {
    for (const [key, value] of Object.entries(incoming)) {
      if (!own(target, key)) target[key] = value;
      else if (object(target[key]) && object(value)) merge(target[key], value);
      else if (JSON.stringify(target[key]) !== JSON.stringify(value)) fail('Conflicting delivery fragments');
    }
  }

  function combine(chunks, multipart) {
    const result = { data: {} };
    let hasData = false;
    chunks.forEach((chunk, index) => {
      noErrors(chunk);
      safeTree(chunk);
      if (own(chunk, 'hasNext') && typeof chunk.hasNext !== 'boolean') fail('Invalid delivery completion');
      if (multipart && !own(chunk, 'hasNext')) fail('Missing delivery completion');
      if (index < chunks.length - 1 && chunk.hasNext === false) fail('Unexpected delivery fragments');
      if (chunk.hasNext === true && index === chunks.length - 1) fail('Incomplete delivery rates');
      if (own(chunk, 'data')) {
        if (!object(chunk.data) || index !== 0) fail('Invalid delivery data');
        merge(result.data, chunk.data);
        hasData = true;
      }
      if (own(chunk, 'incremental')) {
        if (!Array.isArray(chunk.incremental) || !hasData) fail('Invalid delivery fragments');
        for (const patch of chunk.incremental) {
          noErrors(patch);
          // Our query defers one Cart fragment. Reject other paths or streamed
          // list patches instead of losing a delivery group during a merge.
          if (!Array.isArray(patch.path) || patch.path.length !== 1 || patch.path[0] !== 'cart' ||
              !object(patch.data) || !object(result.data.cart) || own(patch, 'items')) {
            fail('Unsupported delivery fragment');
          }
          merge(result.data.cart, patch.data);
        }
      }
    });
    if (!hasData || (multipart && chunks.at(-1)?.hasNext !== false)) fail('Incomplete delivery response');
    return result;
  }

  /** Shopify can serve @defer inline JSON or a completed multipart response. */
  function parseQuoteResponse(text, contentType) {
    if (typeof text !== 'string' || !text.trim() || text.length > 2097152 || typeof contentType !== 'string') {
      fail('Invalid delivery response body');
    }
    const type = contentType.toLowerCase();
    if (/^application\/json(?:\s*;|$)/.test(type)) return combine([JSON.parse(text)], false);
    if (!/^multipart\/mixed(?:\s*;|$)/.test(type)) fail('Unsupported delivery response type');
    const match = contentType.match(/(?:^|;)\s*boundary=(?:"([^"]+)"|([^;\s]+))/i);
    const boundary = match?.[1] || match?.[2];
    if (!boundary || !/^[a-zA-Z0-9'()+_,.\/:=?-]{1,70}$/.test(boundary)) fail('Invalid delivery boundary');
    const lines = text.replace(/\r\n/g, '\n').split('\n');
    const delimiter = '--' + boundary;
    let cursor = 0;
    while (cursor < lines.length && !lines[cursor].trim()) cursor += 1;
    if (lines[cursor] !== delimiter) fail('Invalid delivery multipart body');
    const chunks = [];
    let complete = false;
    while (cursor < lines.length) {
      if (lines[cursor] === delimiter + '--') { complete = true; cursor += 1; break; }
      if (lines[cursor] !== delimiter) fail('Invalid delivery part boundary');
      cursor += 1;
      const headers = [];
      while (cursor < lines.length && lines[cursor] !== '') headers.push(lines[cursor++]);
      if (cursor >= lines.length || headers.filter(header => /^content-type\s*:/i.test(header)).length !== 1 ||
          !headers.some(header => /^content-type:\s*application\/json(?:\s*;.*)?$/i.test(header))) {
        fail('Invalid delivery part headers');
      }
      cursor += 1;
      const body = [];
      while (cursor < lines.length && lines[cursor] !== delimiter && lines[cursor] !== delimiter + '--') {
        body.push(lines[cursor++]);
      }
      if (!body.join('\n').trim()) fail('Empty delivery part');
      chunks.push(JSON.parse(body.join('\n')));
    }
    if (!complete || lines.slice(cursor).some(line => line.trim()) || !chunks.length) fail('Truncated delivery response');
    return combine(chunks, true);
  }

  function quantity(value) {
    if (!Number.isInteger(value) || value < 1 || value > MAX_QUANTITY) fail('Invalid quote quantity');
    return value;
  }

  function gid(value, kind) {
    if (typeof value === 'number' && (!Number.isSafeInteger(value) || value < 1)) fail('Invalid quote identity');
    const text = String(value ?? '');
    if (/^[1-9]\d*$/.test(text)) return `gid://shopify/${kind}/${text}`;
    if (new RegExp(`^gid://shopify/${kind}/[1-9]\\d*$`).test(text)) return text;
    fail('Invalid quote identity');
  }

  function attributes(properties) {
    if (properties == null) return [];
    if (!object(properties)) fail('Unsupported quote properties');
    return Object.entries(properties).map(([key, value]) => {
      if (!key || typeof value !== 'string') fail('Unsupported quote property');
      return { key, value };
    }).sort((a, b) => a.key < b.key ? -1 : a.key > b.key ? 1 : 0);
  }

  function checkedAttributes(values) {
    if (!Array.isArray(values)) fail('Missing quote attributes');
    const seen = new Set();
    const result = values.map(value => {
      if (!object(value) || typeof value.key !== 'string' || !value.key ||
          typeof value.value !== 'string' || seen.has(value.key)) fail('Invalid quote attributes');
      seen.add(value.key);
      return { key: value.key, value: value.value };
    });
    return result.sort((a, b) => a.key < b.key ? -1 : a.key > b.key ? 1 : 0);
  }

  function identity(line) {
    return JSON.stringify([line.merchandiseId, line.sellingPlanId || null, line.attributes]);
  }

  function normaliseLines(lines) {
    if (!Array.isArray(lines) || !lines.length || lines.length > MAX_LINES) fail('Unsupported quote basket size');
    const grouped = new Map();
    for (const line of lines) {
      if (!object(line)) fail('Invalid quote line');
      const next = {
        merchandiseId: gid(line.merchandiseId, 'ProductVariant'),
        quantity: quantity(line.quantity),
        attributes: checkedAttributes(line.attributes),
      };
      if (line.sellingPlanId != null && line.sellingPlanId !== '') next.sellingPlanId = gid(line.sellingPlanId, 'SellingPlan');
      const key = identity(next);
      if (grouped.has(key)) grouped.get(key).quantity = quantity(grouped.get(key).quantity + next.quantity);
      else grouped.set(key, next);
    }
    return [...grouped.values()];
  }

  /** Copy the basket plus the selected item into Storefront inputs. Existing
   * duplicate variants remain distinct when their properties or plan differ. */
  function buildLines(cart, selection) {
    if (!object(cart) || !Array.isArray(cart.items) || cart.items.length > MAX_LINES || !object(selection)) {
      fail('Invalid quote basket');
    }
    const lines = cart.items.map(item => {
      if (!object(item) || item.parent_relationship || item.item_components?.length) fail('Unsupported bundle quote');
      const next = {
        merchandiseId: gid(item.variant_id, 'ProductVariant'),
        quantity: quantity(item.quantity),
        attributes: attributes(item.properties),
      };
      if (item.selling_plan_allocation != null) {
        next.sellingPlanId = gid(item.selling_plan_allocation?.selling_plan?.id, 'SellingPlan');
      }
      return next;
    });
    if (own(cart, 'item_count') && cart.item_count !== lines.reduce((sum, line) => sum + line.quantity, 0)) {
      fail('Incomplete quote basket');
    }
    const next = {
      merchandiseId: gid(selection.variantId, 'ProductVariant'),
      quantity: quantity(selection.quantity),
      attributes: attributes(selection.properties),
    };
    if (selection.sellingPlanId != null && selection.sellingPlanId !== '') {
      next.sellingPlanId = gid(selection.sellingPlanId, 'SellingPlan');
    }
    // Coalesce an identical selection before enforcing Shopify's 250-line cap.
    const combined = [...lines, next];
    if (combined.length > MAX_LINES) {
      const key = identity(next);
      const same = lines.find(line => identity(line) === key);
      if (!same) fail('Unsupported quote basket size');
      same.quantity = quantity(same.quantity + next.quantity);
      return normaliseLines(lines);
    }
    return normaliseLines(combined);
  }

  /** Shopify may adjust an unavailable line instead of returning the requested
   * basket. A successful HTTP response is insufficient proof of a valid quote. */
  function verifyQuoteCart(payload, expectedLines) {
    noErrors(payload);
    const created = payload.data?.cartCreate;
    if (!object(created) || !Array.isArray(created.userErrors) || created.userErrors.length ||
        !Array.isArray(created.warnings) || created.warnings.length) fail('Quote basket was not accepted unchanged');
    const cart = created.cart;
    const expected = normaliseLines(expectedLines);
    if (!object(cart) || typeof cart.id !== 'string' || !cart.id.startsWith('gid://shopify/Cart/') ||
        !object(cart.lines) || !Array.isArray(cart.lines.nodes) || cart.lines.pageInfo?.hasNextPage !== false ||
        !Number.isInteger(cart.totalQuantity) || cart.totalQuantity !== expected.reduce((sum, line) => sum + line.quantity, 0)) {
      fail('Incomplete quote basket response');
    }
    const actual = cart.lines.nodes.map(line => {
      if (!object(line) || !own(line, 'sellingPlanAllocation')) fail('Missing quote line identity');
      const next = { merchandiseId: line.merchandise?.id, quantity: line.quantity, attributes: line.attributes };
      if (line.sellingPlanAllocation !== null) next.sellingPlanId = gid(line.sellingPlanAllocation?.sellingPlan?.id, 'SellingPlan');
      return next;
    });
    const canonical = lines => normaliseLines(lines).map(line => [identity(line), line.quantity]).sort((a, b) => a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0);
    if (JSON.stringify(canonical(actual)) !== JSON.stringify(canonical(expected))) fail('Quote basket differs from requested basket');
    return cart;
  }

  function checkedMoney(money) {
    if (!object(money) || money.currencyCode !== 'AUD' || typeof money.amount !== 'string' ||
        !/^\d+(?:\.\d+)?$/.test(money.amount)) fail('Unverified delivery cost');
    const [whole, fraction = ''] = money.amount.split('.');
    if (fraction.length > 2 && /[1-9]/.test(fraction.slice(2))) fail('Delivery cost contains fractional cents');
    const cents = BigInt(whole) * 100n + BigInt((fraction + '00').slice(0, 2));
    if (cents > BigInt(Number.MAX_SAFE_INTEGER)) fail('Delivery cost is out of range');
    return { amount: money.amount, cents: Number(cents), currency: 'AUD' };
  }

  /** Preserve each delivery group and option. Never quote the cheapest group as
   * the whole basket; recurring shipment costs need a separate supported model. */
  function verifiedGroups(payload) {
    noErrors(payload);
    const groups = payload.data?.cart?.deliveryGroups;
    if (!object(groups) || !Array.isArray(groups.nodes) || !groups.nodes.length ||
        groups.pageInfo?.hasNextPage !== false) fail('Incomplete delivery groups');
    return groups.nodes.map(group => {
      if (!object(group) || group.groupType !== 'ONE_TIME_PURCHASE' ||
          !Array.isArray(group.deliveryOptions) || !group.deliveryOptions.length) fail('Unsupported delivery group');
      return { type: group.groupType, options: group.deliveryOptions.map(option => {
        if (!object(option) || typeof option.title !== 'string' || !option.title.trim() ||
            option.deliveryMethodType !== 'SHIPPING') fail('Unsupported delivery option');
        const result = { title: option.title.trim(), method: option.deliveryMethodType, ...checkedMoney(option.estimatedCost) };
        if (option.code != null) {
          if (typeof option.code !== 'string') fail('Invalid delivery option code');
          result.code = option.code;
        }
        return result;
      }) };
    });
  }

  const host = typeof window === 'undefined' ? globalThis : window;
  host.ETDeliveryCore = Object.freeze({ parseQuoteResponse, verifiedGroups, buildLines, verifyQuoteCart });
})();
